/**
 * "Send at" — the hour the daily procurement summary goes out, in the reader's own
 * timezone. Registered on ModuleNotificationBlock's delivery-option slot, so it
 * renders identically on /procurement/settings and on the central
 * /settings?tab=notifications tab (notification-settings-placement-design.md § 1.1).
 *
 * It shapes HOW an opted-in digest arrives without being a channel toggle, which is
 * what makes it a delivery option rather than a catalog row. The digest's on/off is
 * the catalog row directly above it — this control never renders one, and it is
 * deliberately still shown when the digest is off: hiding it would mean the block
 * re-flows every time somebody toggles the row, and the hour is what they will set
 * next when they turn it back on.
 *
 * Imports the settings HOOK, never the settings page: the barrel that registers this
 * is imported eagerly by every notification mount, and pulling a page in would drag
 * its route-level data hooks along with it.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { useUserPreferences } from '../../../shared/context';
import {
  DEFAULT_DIGEST_SEND_HOUR,
  useProcurementUserSettings,
  useUpdateProcurementUserSettings,
} from '../hooks/useProcurementUserSettings';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const FIELD_ID = 'procurement-daily-summary-send-hour';

export function DailySummarySendHour() {
  const { t } = useTranslation('procurement');
  const { data: bag, isLoading } = useProcurementUserSettings();
  const updateSettings = useUpdateProcurementUserSettings();
  const { formatOptions, timezone } = useUserPreferences();

  const sendHour = bag?.daily_digest_send_hour ?? DEFAULT_DIGEST_SEND_HOUR;

  // Hour-of-day labels, not timestamps — so there is no localized component for
  // them. Formatted through the viewer's own locale + 12h/24h preference from
  // UserPreferences, over an instant pinned to UTC and rendered in UTC, which makes
  // this a pure hour renderer that no timezone can shift.
  const hourOptions = useMemo(
    () =>
      HOURS.map((hour) => ({
        value: String(hour),
        label: new Intl.DateTimeFormat(formatOptions.locale, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: formatOptions.timeFormat === '12h',
          timeZone: 'UTC',
        }).format(new Date(Date.UTC(2000, 0, 1, hour))),
      })),
    [formatOptions.locale, formatOptions.timeFormat]
  );

  if (isLoading) {
    return <Skeleton className="h-9 w-full max-w-md" />;
  }

  // The PUT replaces the whole bag, so the write carries everything already in it.
  const save = (hour: number) =>
    updateSettings.mutate({ ...bag, daily_digest_send_hour: hour });

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="space-y-0.5">
        {/* htmlFor/id rather than an aria-label, so clicking the label text opens
            the menu — matching every other labelled control in this block. */}
        <Label htmlFor={FIELD_ID} className="text-sm font-medium">
          {t('settings.dailySummary.sendAtLabel')}
        </Label>
        <p className="text-xs text-muted-foreground">
          {t('settings.dailySummary.sendAtDescription', { timezone })}
        </p>
      </div>
      <Select value={String(sendHour)} onValueChange={(value) => save(Number(value))}>
        <SelectTrigger id={FIELD_ID} className="w-full sm:w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {hourOptions.map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
